/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ApiResponseHub } from '../models/api-response-hub';
import { ApiResponseListHub } from '../models/api-response-list-hub';
import { ApiResponsePagedResultHub } from '../models/api-response-paged-result-hub';
import { createHub } from '../fn/hub-api/create-hub';
import { CreateHub$Params } from '../fn/hub-api/create-hub';
import { deleteHub } from '../fn/hub-api/delete-hub';
import { DeleteHub$Params } from '../fn/hub-api/delete-hub';
import { getHubById } from '../fn/hub-api/get-hub-by-id';
import { GetHubById$Params } from '../fn/hub-api/get-hub-by-id';
import { getHubByRef } from '../fn/hub-api/get-hub-by-ref';
import { GetHubByRef$Params } from '../fn/hub-api/get-hub-by-ref';
import { listHubs } from '../fn/hub-api/list-hubs';
import { ListHubs$Params } from '../fn/hub-api/list-hubs';
import { searchHubs } from '../fn/hub-api/search-hubs';
import { SearchHubs$Params } from '../fn/hub-api/search-hubs';
import { updateHub } from '../fn/hub-api/update-hub';
import { UpdateHub$Params } from '../fn/hub-api/update-hub';

@Injectable({ providedIn: 'root' })
export class HubApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getHubByRef()` */
  static readonly GetHubByRefPath = '/v1/api/hubs/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getHubByRef()` instead.
   *
   * This method doesn't expect any request body.
   */
  getHubByRef$Response(params: GetHubByRef$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseHub>> {
    return getHubByRef(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getHubByRef$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getHubByRef(params: GetHubByRef$Params, context?: HttpContext): Observable<ApiResponseHub> {
    return this.getHubByRef$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseHub>): ApiResponseHub => r.body)
    );
  }

  /** Path part for operation `updateHub()` */
  static readonly UpdateHubPath = '/v1/api/hubs/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateHub()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateHub$Response(params: UpdateHub$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseHub>> {
    return updateHub(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateHub$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateHub(params: UpdateHub$Params, context?: HttpContext): Observable<ApiResponseHub> {
    return this.updateHub$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseHub>): ApiResponseHub => r.body)
    );
  }

  /** Path part for operation `deleteHub()` */
  static readonly DeleteHubPath = '/v1/api/hubs/{ref}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteHub()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteHub$Response(params: DeleteHub$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteHub(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteHub$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteHub(params: DeleteHub$Params, context?: HttpContext): Observable<void> {
    return this.deleteHub$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `createHub()` */
  static readonly CreateHubPath = '/v1/api/hubs';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createHub()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createHub$Response(params: CreateHub$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseHub>> {
    return createHub(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createHub$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createHub(params: CreateHub$Params, context?: HttpContext): Observable<ApiResponseHub> {
    return this.createHub$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseHub>): ApiResponseHub => r.body)
    );
  }

  /** Path part for operation `searchHubs()` */
  static readonly SearchHubsPath = '/v1/api/hubs/search';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchHubs()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchHubs$Response(params: SearchHubs$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePagedResultHub>> {
    return searchHubs(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchHubs$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchHubs(params: SearchHubs$Params, context?: HttpContext): Observable<ApiResponsePagedResultHub> {
    return this.searchHubs$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePagedResultHub>): ApiResponsePagedResultHub => r.body)
    );
  }

  /** Path part for operation `listHubs()` */
  static readonly ListHubsPath = '/v1/api/hubs/list';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listHubs()` instead.
   *
   * This method doesn't expect any request body.
   */
  listHubs$Response(params: ListHubs$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseListHub>> {
    return listHubs(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listHubs$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listHubs(params: ListHubs$Params, context?: HttpContext): Observable<ApiResponseListHub> {
    return this.listHubs$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseListHub>): ApiResponseListHub => r.body)
    );
  }

  /** Path part for operation `getHubById()` */
  static readonly GetHubByIdPath = '/v1/api/hubs/hub-id/{id}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getHubById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getHubById$Response(params: GetHubById$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseHub>> {
    return getHubById(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getHubById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getHubById(params: GetHubById$Params, context?: HttpContext): Observable<ApiResponseHub> {
    return this.getHubById$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseHub>): ApiResponseHub => r.body)
    );
  }

}
